import { SerializedStyles, css } from "@emotion/react";
import styled from "@emotion/styled";
import { noop } from "lodash";
import * as React from "react";

import { Info } from "@livechat/design-system-icons";
import {
  DesignToken,
  Heading,
  Icon,
  RadioButton,
  RadiusToken,
  SpacingToken,
  Tag,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { Plan } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";

export interface IProps {
  planType: Plan;
  planName: string;
  price: number;
  oldPrice?: number;
  active: boolean;
  disabled: boolean;
  selected: boolean;
  recommended: boolean;
  onClick(plan: Plan): void;
}

export const PlanTile: React.FC<IProps> = ({
  planName,
  planType,
  price,
  active,
  disabled,
  recommended,
  selected,
  oldPrice,
  onClick,
}) => (
  <TileContainer
    active={active}
    disabled={disabled}
    selected={selected}
    recommended={recommended}
    onClick={() => onClick(planType)}
  >
    {active && (
      <Tag size="small" kind="info">
        Your current plan
      </Tag>
    )}

    {recommended && !active && (
      <Tag size="small" kind="success">
        Best for your usage
      </Tag>
    )}

    <SelectedRadioButton checked={selected} disabled={disabled} onChange={noop} />
    <Heading size="xs" css={{ fontWeight: 600 }}>
      {planName}
    </Heading>
    {planType === Plan.Enterprise ? (
      <Text textAlign="center">
        individual contract
        <br />
        with annual billing
      </Text>
    ) : (
      <>
        <StyledPrice size="xl">
          {price}
          {!!oldPrice && <StyledOldPrice>${oldPrice}</StyledOldPrice>}
        </StyledPrice>
        <Text customColor={withVar(DesignToken.ContentBasicSecondary)}>user/month</Text>
        {!!oldPrice && (
          <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)}>
            <Text textAlign="center" customColor={withVar(DesignToken.ActionPositiveDefault)}>
              discount for loyal customers
            </Text>
            <Tooltip
              placement="top"
              triggerRenderer={
                <Icon source={Info} size="small" customColor={withVar(DesignToken.ContentBasicPrimary)} />
              }
              kind="invert"
            >
              We updated our pricing on May 31, 2022, but our loyal customers can still benefit from the previous prices
              under their current plan.
            </Tooltip>
          </Flex>
        )}
      </>
    )}
  </TileContainer>
);

const SelectedRadioButton = styled(RadioButton)`
  position: absolute;
  top: var(${SpacingToken.Spacing3});
  right: var(${SpacingToken.Spacing3});
`;

const TileContainer = styled.div<{
  active: boolean;
  disabled: boolean;
  selected: boolean;
  recommended: boolean;
}>`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 164px;
  border-radius: ${withVar(RadiusToken.Radius3)};
  position: relative;
  gap: ${withVar(SpacingToken.Spacing1)};
  cursor: pointer;

  ${({ disabled }): SerializedStyles | string =>
    disabled
      ? css`
          cursor: not-allowed;
          color: ${withVar(DesignToken.ContentBasicDisabled)};
        `
      : ""}

  ${({ selected }): SerializedStyles =>
    selected
      ? css`
          box-shadow: inset 0 0 0 2px ${withVar(DesignToken.ActionPrimaryDefault)};
        `
      : css`
          box-shadow: inset 0 0 0 1px ${withVar(DesignToken.SurfaceTertiaryDefault)};
        `}
`;

const StyledPrice = styled(Heading)`
  display: flex;
  align-items: center;
  position: relative;

  ::before {
    content: "$";
    color: ${withVar(DesignToken.ContentBasicDisabled)};
    font-size: 18px;
    font-weight: 400;
    left: -12px;
    position: absolute;
  }
`;

const StyledOldPrice = styled.div`
  margin-left: 8px;
  font-weight: 400;
  font-size: 15px;
  line-height: 22px;
  text-decoration-line: line-through;
  color: ${withVar(DesignToken.ContentBasicPrimary)};
`;
